/**
 * The import's document files in Firebase Storage.
 *
 * The upload happens BEFORE the parse now: the server is sent this file's URL
 * instead of its bytes, which used to travel to the worker inside a Redis job
 * argument -- 7-10MB per import, which filled a 25MB instance and wedged the
 * queue. The same file is what a voucher points at afterwards, so a successful
 * accommodation, flight or car rental keeps it.
 *
 * Which files may be deleted is decided by documentImportUploadCleanup, which
 * is plain testable javascript; this module only talks to Firebase.
 */
import firebase from 'firebase/app';
import 'firebase/storage';
import config from '../common/config';
import {
  documentImportFolder, documentImportObjectPath, isDocumentImportPath,
} from './documentImportStoragePath';

/**
 * Upload a document and return where it landed.
 *
 * The folder depends on which backend we are talking to, so running locally
 * does not leave test files among the real documents -- see
 * documentImportStoragePath.
 */
export async function uploadDocumentForImport(file) {
  const folder = documentImportFolder(config.config.ROOT_URL);
  // Unique per upload: two people uploading 'voucher.pdf' in the same
  // millisecond used to share one object, and a shared object means one
  // person's cleanup can delete the other person's voucher.
  const path = documentImportObjectPath(folder, file && file.name);
  const snapshot = await firebase.storage().ref(path).put(file);
  const url = await snapshot.ref.getDownloadURL();
  return { url, path };
}

/**
 * Delete a document we are sure nothing references.
 *
 * Never throws: a file we failed to remove is a few stray KB, while letting it
 * break the import would cost the user their work.
 */
export async function deleteStoredDocument(path) {
  if (!path) return false;
  // Last guard before a destructive call: whatever else goes wrong upstream,
  // this must not be able to remove an attraction photo or a profile picture.
  if (!isDocumentImportPath(path)) {
    console.warn(`[import] refused to delete outside the import folders: ${path}`);
    return false;
  }
  try {
    await firebase.storage().ref(path).delete();
    return true;
  } catch (e) {
    console.warn(`[import] could not delete ${path}:`, e && e.message);
    return false;
  }
}
